import {makeAutoObservable} from 'mobx';
import {toNullableNumber} from '../../../../models/model-types-methods';
import {ICheckboxesTreeItem} from '../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store';
import {ISbrMarginGroup, ISbrMarginGroupSelection} from '../types';
import SbrMarginGroupStore from './sbr-margin-group-store';

export default class SbrMarginGroupsEditorStore {
    private _groupStores: SbrMarginGroupStore[] = [];

    constructor(
        private readonly _shipmentOptions: ICheckboxesTreeItem[],
        groups: ISbrMarginGroup[] = [],
    ) {
        this.setGroups(groups);
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public reset(groups: ISbrMarginGroup[] = []): void {
        this.setGroups(groups);
    }

    private setGroups(groups: ISbrMarginGroup[]): void {
        this._groupStores = this.normalizeSbrMarginGroups(groups).map(
            (group) => new SbrMarginGroupStore(group, this._shipmentOptions)
        );
    }

    public getGroups(): SbrMarginGroupStore[] {
        return this._groupStores.filter((group) => !group.deleted());
    }

    public getShipmentOptions(): ICheckboxesTreeItem[] {
        return this._shipmentOptions;
    }

    public addGroup(): void {
        this._groupStores.push(new SbrMarginGroupStore(undefined, this._shipmentOptions));
    }

    public removeGroup(clientId: string): void {
        const group = this._groupStores.find((item) => item.clientId === clientId);
        if (!group) {
            return;
        }

        if (group.getId()) {
            group.setDeleted(true);
            return;
        }

        this._groupStores = this._groupStores.filter((item) => item.clientId !== clientId);
    }

    public hasChanges(): boolean {
        return this._groupStores.some((group) => group.hasChanges());
    }

    public getDuplicateSelectionKeys(): Map<string, string[]> {
        return this.findDuplicateSelectionKeys(
            this.getGroups().map((group) => ({
                clientId: group.clientId,
                selections: group.getSelections(),
            }))
        );
    }

    public hasDuplicateSelections(): boolean {
        return this.getDuplicateSelectionKeys().size > 0;
    }

    public getDuplicateSelectionsMessage(): string | undefined {
        const duplicateKeys = this.getDuplicateSelectionKeys();
        const labels = new Set<string>();

        this.getGroups().forEach((group) => {
            group.getSelections().forEach((selection) => {
                const key = this.getSelectionKey(selection.carrier_code, selection.service_code);
                if ((duplicateKeys.get(key)?.length ?? 0) > 1) {
                    labels.add(this.formatSelectionLabel(selection));
                }
            });
        });

        if (!labels.size) {
            return undefined;
        }

        return Array.from(labels).join(', ');
    }

    public hasIncompleteGroups(): boolean {
        return this.getGroups().some((group) => group.hasIncompleteFields());
    }

    public getUpdatedModel(): ISbrMarginGroup[] {
        return this._groupStores
            .map((group) => group.getUpdatedModel())
            .filter((group): group is ISbrMarginGroup => group !== null);
    }

    private getSelectionKey(carrierCode: string, serviceCode: string): string {
        return `${carrierCode}::${serviceCode}`;
    }

    private formatSelectionLabel(selection: ISbrMarginGroupSelection): string {
        const carrierOption = this._shipmentOptions.find((option) => option.value === selection.carrier_code);
        const carrierName = carrierOption?.label ?? selection.carrier_code;
        const serviceOption = carrierOption?.childNodes?.find((service) => service.value === selection.service_code);
        const serviceName = serviceOption?.label ?? selection.service_code;
        return `${carrierName} - ${serviceName}`;
    }

    private findDuplicateSelectionKeys(
        groups: Array<{ clientId: string; selections: ISbrMarginGroupSelection[] }>
    ): Map<string, string[]> {
        const keyToGroupIds = new Map<string, string[]>();

        groups.forEach((group) => {
            group.selections.forEach((selection) => {
                const key = this.getSelectionKey(selection.carrier_code, selection.service_code);
                const groupIds = keyToGroupIds.get(key) ?? [];
                groupIds.push(group.clientId);
                keyToGroupIds.set(key, groupIds);
            });
        });

        const duplicates = new Map<string, string[]>();
        keyToGroupIds.forEach((groupIds, key) => {
            if (groupIds.length > 1) {
                duplicates.set(key, groupIds);
            }
        });

        return duplicates;
    }

    private normalizeSbrMarginGroups(groups: ISbrMarginGroup[] | undefined): ISbrMarginGroup[] {
        return (groups ?? []).map((group) => ({
            id: group.id,
            fixed_margin: toNullableNumber(group.fixed_margin),
            relative_margin: toNullableNumber(group.relative_margin),
            selections: group.selections ?? [],
        }));
    }
}
